/**
 * Buses anywhere, from OpenStreetMap: its bus routes (`route=bus`
 * relations), each a line's way and its stops in one direction, and a
 * timetable made up for them. Where there is no live timetable (outside
 * Västtrafik's area, or without its credentials) these are the buses.
 *
 * The routes come from Overpass with their members' geometry cut to a box
 * about the player (`overpassRoutesQuery`), so a line 50 km long costs only
 * its few kilometres here. Its ways are chained end to end into one way, in
 * the relation's order, turned round where the map drew one backwards; its
 * stops are the members in a stop's role, or else its platforms.
 *
 * The timetable: a bus every `headway` minutes, by the hour (`headwayAt`), or
 * the route's own `interval` where it has one, from a time of the day
 * chosen by the route's id, so each poll makes the same buses. It drives
 * its way at `RUNNING` and stands `STOP_SECONDS` at each stop.
 *
 * Pure: no fetch, no DOM.
 */

import type { BusLook, TransitTrip } from './trip.js';

export interface OsmRoute {
  id: number;
  /** Its number, as on the front: `ref`, else its name. */
  ref: string;
  /** Where it goes: `to`, else the end of its name. */
  to: string;
  /** `colour`, as #rrggbb, if it has one. */
  colour: string | undefined;
  /** `interval`, minutes, if it has one. */
  interval: number | undefined;
  /** lat, lon pairs along its way in the box. */
  path: number[];
  stops: Array<{ lat: number; lon: number; name: string }>;
}

/** Overpass QL for the bus routes in a box, their members' geometry cut to it. */
export function overpassRoutesQuery(south: number, west: number, north: number, east: number): string {
  const box = `${south.toFixed(5)},${west.toFixed(5)},${north.toFixed(5)},${east.toFixed(5)}`;
  return `[out:json][timeout:25];relation["type"="route"]["route"="bus"](${box});out geom(${box});`;
}

interface OverpassMember { type: string; role: string; lat?: number; lon?: number; geometry?: Array<{ lat: number; lon: number } | null> }
interface OverpassRelation { type: string; id: number; tags?: Record<string, string>; members?: OverpassMember[] }

const STOP_ROLE = /^stop(_entry_only|_exit_only)?$/;
const PLATFORM_ROLE = /^platform(_entry_only|_exit_only)?$/;
/** Ends this close are one join, metres; further, the way breaks there. */
const JOIN = 25;

const metres = (lat0: number) => [111_320 * Math.cos(lat0 * Math.PI / 180), 110_540] as const;

/** The bus routes in an Overpass answer. */
export function parseRoutes(data: unknown): OsmRoute[] {
  const elements = (data as { elements?: unknown } | null)?.elements;
  if (!Array.isArray(elements)) return [];
  const out: OsmRoute[] = [];
  for (const e of elements as OverpassRelation[]) {
    if (e.type !== 'relation' || e.members === undefined) continue;
    const tags = e.tags ?? {};
    const ways = e.members.filter(m => m.type === 'way' && m.role === '' && m.geometry !== undefined)
      .map(m => (m.geometry ?? []).filter((p): p is { lat: number; lon: number } => p !== null).flatMap(p => [p.lat, p.lon]))
      .filter(g => g.length >= 4);
    const path = chain(ways);
    if (path.length < 4) continue;
    const nodes = e.members.filter(m => m.type === 'node' && m.lat !== undefined && m.lon !== undefined);
    let stops = nodes.filter(m => STOP_ROLE.test(m.role));
    if (stops.length < 2) stops = nodes.filter(m => PLATFORM_ROLE.test(m.role));
    const name = tags.name ?? '';
    out.push({
      id: e.id,
      ref: tags.ref ?? name,
      to: tags.to ?? (name.includes('=>') ? name.split('=>').pop()!.trim() : name.includes('→') ? name.split('→').pop()!.trim() : ''),
      colour: /^#[0-9a-f]{6}$/i.test(tags.colour ?? '') ? tags.colour!.toLowerCase() : undefined,
      interval: minutesOf(tags.interval),
      path,
      stops: stops.map(m => ({ lat: m.lat!, lon: m.lon!, name: '' })),
    });
  }
  return out;
}

/**
 * The routes in one of Sweden's route tiles (tools/osmsweden/routes.py): the
 * same as Overpass gives, made from the extract with each route's ways and
 * stops in its own order, cut to the tile and a margin round it.
 */
export function readRouteTile(data: unknown): OsmRoute[] {
  const routes = (data as { routes?: unknown } | null)?.routes;
  if (!Array.isArray(routes)) return [];
  const out: OsmRoute[] = [];
  for (const r of routes as Array<Record<string, unknown>>) {
    const path = r.path, stops = r.stops;
    if (typeof r.id !== 'number' || !Array.isArray(path) || path.length < 4 || !Array.isArray(stops) || stops.length < 2) continue;
    const name = typeof r.name === 'string' ? r.name : '';
    const colour = typeof r.colour === 'string' ? r.colour : '';
    out.push({
      id: r.id,
      ref: typeof r.ref === 'string' ? r.ref : name,
      to: typeof r.to === 'string' ? r.to : (name.includes('=>') ? name.split('=>').pop()!.trim() : ''),
      colour: /^#[0-9a-f]{6}$/i.test(colour) ? colour.toLowerCase() : undefined,
      interval: minutesOf(typeof r.interval === 'string' ? r.interval : undefined),
      path: path as number[],
      stops: (stops as Array<[number, number, string]>).map(([lat, lon, stopName]) => ({ lat, lon, name: stopName ?? '' })),
    });
  }
  return out;
}

/** `interval` as minutes: "15", "00:15", "PT15M". */
function minutesOf(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const hm = /^(\d{1,2}):(\d{2})/.exec(value);
  if (hm !== null) return Number(hm[1]) * 60 + Number(hm[2]);
  const iso = /^PT(?:(\d+)H)?(?:(\d+)M)?$/i.exec(value);
  if (iso !== null) return Number(iso[1] ?? 0) * 60 + Number(iso[2] ?? 0);
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * The ways end to end, in order, each turned to run on from the last. Where
 * one does not join, the longest stretch that does is kept.
 */
export function chain(ways: number[][]): number[] {
  if (ways.length === 0) return [];
  const [mx, mz] = metres(ways[0][0]);
  const gap = (a: number[], ai: number, b: number[], bi: number) =>
    Math.hypot((a[ai + 1] - b[bi + 1]) * mx, (a[ai] - b[bi]) * mz);
  const reverse = (w: number[]) => {
    const r: number[] = [];
    for (let i = w.length - 2; i >= 0; i -= 2) r.push(w[i], w[i + 1]);
    return r;
  };
  let best: number[] = [], run: number[] = [];
  for (let k = 0; k < ways.length; k++) {
    let w = ways[k];
    if (run.length === 0) {
      // The first way's direction is the one that meets the next.
      const next = ways[k + 1];
      if (next !== undefined) {
        const endToNext = Math.min(gap(w, w.length - 2, next, 0), gap(w, w.length - 2, next, next.length - 2));
        const startToNext = Math.min(gap(w, 0, next, 0), gap(w, 0, next, next.length - 2));
        if (startToNext < endToNext) w = reverse(w);
      }
      run = w.slice();
      continue;
    }
    const end = run.length - 2;
    if (gap(run, end, w, 0) <= JOIN) run.push(...w.slice(2));
    else if (gap(run, end, w, w.length - 2) <= JOIN) run.push(...reverse(w).slice(2));
    else {
      if (run.length > best.length) best = run;
      run = w.slice();
    }
  }
  return run.length > best.length ? run : best;
}

/** Minutes between buses at an hour of the day; Infinity when none run. */
export function headwayAt(hour: number): number {
  if (hour >= 6 && hour < 19) return 15;
  if (hour >= 19 && hour < 23) return 30;
  if (hour >= 5 && hour < 6) return 30;
  return Infinity;
}

/** Driving between stops, m/s: about 22 km/h, a town bus's pace with its junctions and lights. */
const RUNNING = 6.2;
/** Standing at each stop, seconds. */
const STOP_SECONDS = 20;

/**
 * The trips on a route about `now` (ms), at `hour` local: every bus whose
 * time on the way in the box covers now, give or take a few minutes.
 * `look` is the design its buses wear.
 */
export function scheduleRoute(route: OsmRoute, now: number, hour: number, look: BusLook): TransitTrip[] {
  const headway = route.interval ?? headwayAt(hour);
  if (!Number.isFinite(headway) || route.stops.length < 2) return [];
  const step = headway * 60_000;
  // How far along the way each stop is, and so when each is reached.
  const [mx, mz] = metres(route.path[0]);
  const along: number[] = [0];
  for (let i = 2; i < route.path.length; i += 2) {
    along.push(along[along.length - 1] + Math.hypot((route.path[i + 1] - route.path[i - 1]) * mx, (route.path[i] - route.path[i - 2]) * mz));
  }
  const stopAlong = route.stops.map(s => {
    let best = Infinity, at = 0;
    for (let i = 0; i < along.length; i++) {
      const d = Math.hypot((route.path[i * 2 + 1] - s.lon) * mx, (route.path[i * 2] - s.lat) * mz);
      if (d < best) { best = d; at = along[i]; }
    }
    return at;
  });
  const order = stopAlong.map((a, i) => ({ a, i })).sort((p, q) => p.a - q.a);
  const span = (along[along.length - 1] / RUNNING + STOP_SECONDS * route.stops.length) * 1000;
  // Departures on a beat the route's id sets, so the same buses each time.
  const phase = ((route.id * 2654435761) >>> 0) % step;
  const trips: TransitTrip[] = [];
  for (let t0 = Math.floor((now - span - phase) / step) * step + phase; t0 <= now + 5 * 60_000; t0 += step) {
    if (t0 + span < now - 60_000) continue;
    const stops = order.map(({ a, i }, k) => {
      const arrive = t0 + (a / RUNNING + k * STOP_SECONDS) * 1000;
      return { ...route.stops[i], arrive, depart: arrive + (k === 0 ? 0 : STOP_SECONDS * 1000) };
    });
    trips.push({
      id: `osm:${route.id}:${t0}`,
      line: { name: route.ref, mode: 'bus', look, background: route.colour ?? '#c8102e', foreground: '#ffffff', border: route.colour ?? '#c8102e' },
      headsign: route.to,
      stops,
      path: route.path,
    });
  }
  return trips;
}
